import { create } from 'zustand';
import type { User } from '../types/auth';
import { clearTokens, setWalletAddress } from '../utils/token';
import { authService } from '../services/authService';
import { normalizeRoleName, normalizeRoleNames } from '../utils/roles';

const AUTH_SESSION_HINT_KEY = 'kxdex_auth_session_hint';

function extractRoleNames(user: User | null): string[] {
  return normalizeRoleNames((user?.roles || []).map((role) => role.name));
}

function hasSessionHint(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return Boolean(window.localStorage.getItem(AUTH_SESSION_HINT_KEY));
}

function setSessionHint(username?: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(AUTH_SESSION_HINT_KEY, username || '1');
}

function clearSessionHint(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(AUTH_SESSION_HINT_KEY);
}

interface AuthState {
  user: User | null;
  token: string | null;
  roles: string[];
  isAuthenticated: boolean;
  isLoading: boolean;

  setAuth: (user: User) => void;
  clearAuth: () => void;
  initFromStorage: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  roles: [],
  isAuthenticated: false,
  isLoading: true,

  setAuth: (user) => {
    if (user.walletAddress) {
      setWalletAddress(user.walletAddress);
    }
    setSessionHint(user.username);
    set({
      user,
      token: null,
      roles: extractRoleNames(user),
      isAuthenticated: true,
      isLoading: false,
    });
  },

  clearAuth: () => {
    clearTokens();
    clearSessionHint();
    set({
      user: null,
      token: null,
      roles: [],
      isAuthenticated: false,
      isLoading: false,
    });
  },

  initFromStorage: async () => {
    set({ isLoading: true });

    try {
      const session = await authService.checkSession();
      if (session.valid && session.user) {
        get().setAuth(session.user);
        return;
      }
    } catch {
      // Fall through to refresh flow
    }

    if (hasSessionHint()) {
      try {
        const refresh = await authService.refreshToken();
        if (refresh.refreshed) {
          const session = await authService.checkSession();
          if (session.valid && session.user) {
            get().setAuth(session.user);
            return;
          }
        }
      } catch {
        // Ignore and clear local auth state below
      }
    }

    clearTokens();
    clearSessionHint();
    set({
      user: null,
      token: null,
      roles: [],
      isAuthenticated: false,
      isLoading: false,
    });
  },

  setLoading: (loading) => set({ isLoading: loading }),

  hasRole: (role) => get().roles.includes(normalizeRoleName(role)),

  hasPermission: (permission) => {
    const { user } = get();
    return user?.permissions?.includes(permission) ?? false;
  },
}));
